import { Node } from 'reactflow';
import useStore from '@components/MainStage/store';
import { SUB_LAYER_PADDING_Y } from '@constants/canvas/general';
import {
  LayerSubLayers,
  LayerTypes,
  subLayerParent,
  SubLayerTypes,
} from '@constants/canvas/layers';
import {
  MIN_ROWS_COUNT,
  SUB_LAYER_FULL_PADDING_Y,
  SUB_LAYER_MIN_HEIGHT,
} from '@utils/canvas/layerHeights';
import LayerViewBase from '@utils/canvas/LayerViewBase';
import NodeView from '@utils/canvas/NodeView';
import { getSubLayerNodes } from '@utils/canvas/subLayerNodesIndex';

const buildRowsMatrix = (nodes: readonly Node[]): Node[][] =>
  nodes.reduce((rows, node) => {
    const rowIndex = NodeView.calculateRowByY(node.position.y);

    if (!rows[rowIndex]) rows[rowIndex] = [];
    rows[rowIndex].push(node);

    return rows;
  }, [] as Node[][]);

/**
 * One sub-layer of the canvas: the nodes inside it and the rows they occupy.
 *
 * Both are derived on read and cached against the identity of the `nodes`
 * array, so a store write that changes neither membership nor row layout — a
 * label edit, for instance — costs nothing here.
 *
 * Passing `nodes` pins the view to that set instead of the live canvas, which
 * is how report layout is measured off-canvas.
 */
class SublayerView extends LayerViewBase<SubLayerTypes> {
  static readonly PaddingY: number = SUB_LAYER_PADDING_Y;

  static readonly FullPaddingY: number = SUB_LAYER_FULL_PADDING_Y;

  static readonly MinRowsCount: number = MIN_ROWS_COUNT;

  static readonly InitialHeight: number = SUB_LAYER_MIN_HEIGHT;

  private readonly fixedNodes: Node[] | null;

  private cachedRowsSource: readonly Node[] | null = null;

  private cachedRowsMatrix: Node[][] = [];

  constructor(id: SubLayerTypes, nodes?: Node[]) {
    super(id);

    this.fixedNodes = nodes
      ? nodes.filter((node) => node.parentNode === id)
      : null;
  }

  get nodes(): readonly Node[] {
    return (
      this.fixedNodes ?? getSubLayerNodes(useStore.getState().nodes, this.id)
    );
  }

  get rowsMatrix(): Node[][] {
    const { nodes } = this;

    if (nodes !== this.cachedRowsSource) {
      this.cachedRowsSource = nodes;
      this.cachedRowsMatrix = buildRowsMatrix(nodes);
    }

    return this.cachedRowsMatrix;
  }

  hasSelected() {
    return this.nodes.some(({ selected }) => selected);
  }

  hasVisibleNodes(filteredNodes: Node[]) {
    return filteredNodes.some((node) => node.id === this.id && !node.hidden);
  }

  /*
      Return rows count based on nodes in store
      Sublayer should have at least one row. Even if there are no nodes.
   */
  get rowsCount(): number {
    return this.rowsMatrix.length || SublayerView.MinRowsCount;
  }

  get parentLayerId(): LayerTypes {
    return subLayerParent[this.id];
  }

  /*
      We need to collapse rows to remove empty rows.
      We should move nodes to the top of the sublayer, so there are no empty rows.
   */
  collapseRows(): void {
    const filledRows = this.rowsMatrix.filter((row) => row.length);

    filledRows.forEach((rowToMove, rowIndex) => {
      rowToMove.forEach((nodeToMove) => {
        new NodeView(nodeToMove.id, nodeToMove).moveToRow(rowIndex);
      });
    });
  }

  dispose(): void {
    super.dispose();

    this.cachedRowsSource = null;
    this.cachedRowsMatrix = [];
  }

  static isSubLayer<T extends string>(id: T) {
    return Object.values(LayerSubLayers).some((subLayerKeys) =>
      subLayerKeys.includes(id as any),
    );
  }
}

export default SublayerView;
